// Kerangka CMS: sidebar dari GET /admin/me/menus (menu tanpa izin READ tidak tampil).
import type { AdminMenuCode } from '@folaplus/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { Fragment, Suspense, useState, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';

import { api } from '../lib/api';
import { MENU_LABEL } from '../lib/labels';
import { useSession } from '../lib/session';
import { Loading } from './ui';
import { ViewModeShield } from './ViewModeShield';

export const MENU_PATH: Record<AdminMenuCode, string> = {
  DASHBOARD: '/dashboard',
  MEMBERS: '/member',
  CONFIG_MEMBERSHIP: '/konfigurasi/membership',
  CONFIG_TIER: '/konfigurasi/tier',
  CONFIG_INCENTIVE: '/konfigurasi/incentive',
  CONFIG_ACCOUNT_WALLET: '/konfigurasi/account_wallet',
  CONFIG_PAYMENT_TAX: '/konfigurasi/payment',
  PLATFORMS: '/platform',
  SHOPPING_ORDERS: '/transaksi-belanja',
  PACKAGE_ORDERS: '/transaksi-paket',
  WALLET_LEDGER: '/wallet',
  WITHDRAWALS: '/pencairan',
  FINANCE: '/keuangan',
  TAX: '/pajak',
  REPORTS: '/laporan',
  AUDIT_LOGS: '/audit-log',
  ADMIN_ROLES: '/admin-role',
};

const MENU_ICONS: Record<AdminMenuCode, ReactNode> = {
  DASHBOARD: (
    <svg className="size-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
      />
    </svg>
  ),
  MEMBERS: (
    <svg className="size-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
      />
    </svg>
  ),
  CONFIG_MEMBERSHIP: (
    <svg className="size-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
      />
    </svg>
  ),
  CONFIG_TIER: (
    <svg className="size-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
    </svg>
  ),
  CONFIG_INCENTIVE: (
    <svg className="size-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  ),
  CONFIG_ACCOUNT_WALLET: (
    <svg className="size-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
      />
    </svg>
  ),
  CONFIG_PAYMENT_TAX: (
    <svg className="size-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
      />
    </svg>
  ),
  PLATFORMS: (
    <svg className="size-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
      />
    </svg>
  ),
  SHOPPING_ORDERS: (
    <svg className="size-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
      />
    </svg>
  ),
  PACKAGE_ORDERS: (
    <svg className="size-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
      />
    </svg>
  ),
  WALLET_LEDGER: (
    <svg className="size-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
      />
    </svg>
  ),
  WITHDRAWALS: (
    <svg className="size-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
      />
    </svg>
  ),
  FINANCE: (
    <svg className="size-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
      />
    </svg>
  ),
  TAX: (
    <svg className="size-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2zM10 8.5a.5.5 0 11-1 0 .5.5 0 011 0zm5 5a.5.5 0 11-1 0 .5.5 0 011 0z"
      />
    </svg>
  ),
  REPORTS: (
    <svg className="size-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
    </svg>
  ),
  AUDIT_LOGS: (
    <svg className="size-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  ),
  ADMIN_ROLES: (
    <svg className="size-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
      />
    </svg>
  ),
};

const CONFIG_ICON = (
  <svg className="size-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
    />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

type NavLeaf = { menu: AdminMenuCode; short?: string };
type NavGroup = { id: string; label: string; icon: ReactNode; items: NavLeaf[] };
type NavEntry = NavLeaf | NavGroup;
type NavSection = { header?: string; entries: NavEntry[] };

const NAVIGATION: NavSection[] = [
  { entries: [{ menu: 'DASHBOARD' }] },
  {
    header: 'Member & transaksi',
    entries: [{ menu: 'MEMBERS' }, { menu: 'SHOPPING_ORDERS' }, { menu: 'PACKAGE_ORDERS' }],
  },
  {
    header: 'Keuangan',
    entries: [{ menu: 'WALLET_LEDGER' }, { menu: 'WITHDRAWALS' }, { menu: 'FINANCE' }, { menu: 'TAX' }],
  },
  {
    header: 'Pengaturan',
    entries: [
      {
        id: 'config',
        label: 'Konfigurasi',
        icon: CONFIG_ICON,
        items: [
          { menu: 'CONFIG_MEMBERSHIP', short: 'Membership' },
          { menu: 'CONFIG_TIER', short: 'Tier' },
          { menu: 'CONFIG_INCENTIVE', short: 'Insentif' },
          { menu: 'CONFIG_ACCOUNT_WALLET', short: 'Wallet & akun' },
          { menu: 'CONFIG_PAYMENT_TAX', short: 'Pembayaran & pajak' },
        ],
      },
      { menu: 'PLATFORMS' },
    ],
  },
  {
    header: 'Laporan & sistem',
    entries: [{ menu: 'REPORTS' }, { menu: 'AUDIT_LOGS' }, { menu: 'ADMIN_ROLES' }],
  },
];

const isGroup = (entry: NavEntry): entry is NavGroup => 'items' in entry;

function visibleNavigation(allowed: AdminMenuCode[]): NavSection[] {
  const allow = new Set(allowed);
  const placed = new Set<AdminMenuCode>();
  const sections: NavSection[] = [];
  for (const section of NAVIGATION) {
    const entries: NavEntry[] = [];
    for (const entry of section.entries) {
      if (!isGroup(entry)) {
        placed.add(entry.menu);
        if (allow.has(entry.menu)) entries.push(entry);
        continue;
      }
      entry.items.forEach((item) => placed.add(item.menu));
      const items = entry.items.filter((item) => allow.has(item.menu));
      if (items.length === 1) entries.push({ menu: items[0]!.menu });
      else if (items.length > 1) entries.push({ ...entry, items });
    }
    if (entries.length > 0) sections.push({ ...section, entries });
  }
  const rest = allowed.filter((menu) => !placed.has(menu));
  if (rest.length > 0) sections.push({ header: 'Lainnya', entries: rest.map((menu) => ({ menu })) });
  return sections;
}

const SIDEBAR_KEY = 'fola.cms.sidebar-collapsed';

function useSidebarCollapsed(): [boolean, () => void] {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_KEY) === '1';
    } catch {
      return false;
    }
  });
  const toggle = () =>
    setCollapsed((current) => {
      const next = !current;
      try {
        localStorage.setItem(SIDEBAR_KEY, next ? '1' : '0');
      } catch {
        // Diabaikan: preferensi hanya berlaku untuk tab ini.
      }
      return next;
    });
  return [collapsed, toggle];
}

const ITEM_BASE =
  'group/item mx-3.5 flex w-[calc(100%-1.75rem)] items-center gap-3 rounded-md px-4 py-2.5 text-[0.9rem] transition-colors';
const ITEM_ACTIVE =
  'bg-linear-118 from-[#3851F0] to-[#3851F0]/70 text-white shadow-[0_0_10px_1px_rgba(56,81,240,0.45)]';
const ITEM_IDLE = 'text-slate-600 hover:text-slate-900';
const NUDGE =
  'flex min-w-0 flex-1 items-center gap-3 transition-transform duration-200 group-hover/item:translate-x-[5px]';

function SidebarLink({
  menu,
  label,
  mini,
  sub = false,
  onNavigate,
}: {
  menu: AdminMenuCode;
  label: string;
  mini: boolean;
  sub?: boolean;
  onNavigate: () => void;
}) {
  const fullLabel = MENU_LABEL[menu];
  return (
    <NavLink
      to={MENU_PATH[menu]}
      onClick={onNavigate}
      aria-label={label === fullLabel ? undefined : fullLabel}
      title={mini ? fullLabel : undefined}
      className={({ isActive }) => `${ITEM_BASE} ${isActive ? ITEM_ACTIVE : ITEM_IDLE}`}
    >
      <span className={NUDGE}>
        {sub ? (
          <span className="flex w-5 shrink-0 justify-center" aria-hidden="true">
            <span className="size-[9px] rounded-full border-[1.5px] border-current" />
          </span>
        ) : (
          MENU_ICONS[menu]
        )}
        <span className={`truncate ${mini ? 'md:sr-only' : ''}`}>{label}</span>
      </span>
    </NavLink>
  );
}

function SidebarGroup({
  group,
  open,
  mini,
  onToggle,
  onNavigate,
}: {
  group: NavGroup;
  open: boolean;
  mini: boolean;
  onToggle: () => void;
  onNavigate: () => void;
}) {
  const { pathname } = useLocation();
  const hasActive = group.items.some((item) => pathname.startsWith(MENU_PATH[item.menu]));
  const listId = `cms-nav-${group.id}`;

  const tone =
    mini && hasActive
      ? `${ITEM_ACTIVE} md:shadow-none`
      : open || hasActive
        ? 'bg-slate-100 text-slate-900'
        : ITEM_IDLE;
  return (
    <li>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={listId}
        title={mini ? group.label : undefined}
        className={`${ITEM_BASE} cursor-pointer ${tone}`}
      >
        <span className={NUDGE}>
          {group.icon}
          <span className={`truncate text-left ${mini ? 'md:sr-only' : ''}`}>{group.label}</span>
        </span>
        <svg
          className={`size-4 shrink-0 transition-transform duration-200 ${open ? 'rotate-90' : ''} ${mini ? 'md:hidden' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>
      <ul id={listId} className={`mt-1 space-y-1 ${open ? (mini ? 'md:hidden' : '') : 'hidden'}`}>
        {group.items.map((item) => (
          <li key={item.menu}>
            <SidebarLink
              menu={item.menu}
              label={item.short ?? MENU_LABEL[item.menu]}
              mini={mini}
              sub
              onNavigate={onNavigate}
            />
          </li>
        ))}
      </ul>
    </li>
  );
}

export function Layout() {
  const { me, menus, isViewMode } = useSession();
  const [collapsed, toggleSidebar] = useSidebarCollapsed();

  const [peek, setPeek] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [groupToggles, setGroupToggles] = useState<Record<string, boolean>>({});
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const mini = collapsed && !peek;

  async function logout() {
    try {
      await api('logout');
    } finally {
      queryClient.clear();
      void navigate('/masuk', { replace: true });
    }
  }

  // Find active menu for header breadcrumb
  const currentMenu = menus.find((m) => location.pathname.startsWith(MENU_PATH[m.menu]));
  const currentLabel = currentMenu ? MENU_LABEL[currentMenu.menu] : 'Dashboard';

  const sections = visibleNavigation(menus.map((m) => m.menu));
  const closeMobile = () => setMobileOpen(false);

  const isGroupOpen = (group: NavGroup) =>
    groupToggles[group.id] ?? group.items.some((item) => location.pathname.startsWith(MENU_PATH[item.menu]));

  const shell = (
    <div className="flex min-h-dvh bg-slate-50/70">
      {mobileOpen ? (
        <div
          className="fixed inset-0 z-40 bg-slate-900/50 md:hidden"
          onClick={closeMobile}
          aria-hidden="true"
        />
      ) : null}

      <div
        className={`shrink-0 transition-[width] duration-300 md:sticky md:z-30 md:top-0 md:h-dvh ${collapsed ? 'md:w-20' : 'md:w-[260px]'}`}
      >
        <aside
          id="cms-sidebar"
          onMouseEnter={() => collapsed && setPeek(true)}
          onMouseLeave={() => setPeek(false)}
          onFocus={() => collapsed && setPeek(true)}
          onBlur={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget)) setPeek(false);
          }}
          onKeyDown={(e) => e.key === 'Escape' && closeMobile()}
          className={`fixed inset-y-0 left-0 z-50 flex w-[260px] flex-col bg-white md:absolute transition-[width,transform,box-shadow] duration-300 ease-[cubic-bezier(0.25,0.8,0.25,1)] md:translate-x-0 ${
            mobileOpen ? 'translate-x-0 shadow-xl' : '-translate-x-full'
          } ${mini ? 'md:w-20' : 'md:w-[260px]'} ${
            collapsed && peek
              ? 'md:shadow-[0_0_15px_0_rgba(34,41,47,0.18)]'
              : 'md:shadow-[0_0_15px_0_rgba(34,41,47,0.05)]'
          }`}
        >
          <div className="flex h-[4.75rem] shrink-0 items-center gap-3 pr-4 pl-[1.375rem]">
            <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-tr from-[#E757F1] to-[#3851F0] text-xs font-black text-white shadow-md shadow-indigo-500/20">
              F+
            </span>
            <p
              className={`min-w-0 truncate text-[1.35rem] font-semibold tracking-tight text-[#3851F0] ${mini ? 'md:hidden' : ''}`}
            >
              Fola+ <span className="text-xs font-medium tracking-normal text-slate-400">CMS</span>
            </p>
            <button
              type="button"
              onClick={toggleSidebar}
              aria-controls="cms-sidebar"
              aria-pressed={!collapsed}
              title={collapsed ? 'Kunci sidebar lebar' : 'Ciutkan sidebar'}
              className={`ml-auto hidden size-8 shrink-0 cursor-pointer items-center justify-center rounded-full text-[#3851F0] transition hover:bg-[#3851F0]/10 ${
                mini ? '' : 'md:flex'
              }`}
            >
              <span className="sr-only">{collapsed ? 'Kunci sidebar lebar' : 'Ciutkan sidebar'}</span>
              <svg
                className="size-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="9" strokeWidth={2} />
                {collapsed ? null : <circle cx="12" cy="12" r="3" strokeWidth={2} />}
              </svg>
            </button>
            <button
              type="button"
              onClick={closeMobile}
              aria-label="Tutup menu"
              className="ml-auto flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 md:hidden"
            >
              <svg
                className="size-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div
            aria-hidden="true"
            className={`pointer-events-none absolute inset-x-0 top-[4.75rem] z-10 h-10 bg-linear-to-b from-white from-40% to-white/0 transition-opacity ${
              scrolled ? 'opacity-100' : 'opacity-0'
            }`}
          />

          <nav
            aria-label="Menu CMS"
            onScroll={(e) => setScrolled(e.currentTarget.scrollTop > 0)}
            className="flex-1 overflow-x-hidden overflow-y-auto pb-5"
          >
            <ul className="space-y-1" data-testid="sidebar">
              {sections.map((section, index) => (
                <Fragment key={section.header ?? index}>
                  {section.header ? (
                    <li className="pt-5 pb-1.5 pl-[1.875rem] text-[0.7rem] font-medium tracking-[0.06em] text-slate-400 uppercase">
                      <span className={mini ? 'md:sr-only' : ''}>{section.header}</span>
                      <svg
                        className={`hidden size-5 ${mini ? 'md:block' : ''}`}
                        fill="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <circle cx="5" cy="12" r="1.5" />
                        <circle cx="12" cy="12" r="1.5" />
                        <circle cx="19" cy="12" r="1.5" />
                      </svg>
                    </li>
                  ) : null}
                  {section.entries.map((entry) =>
                    isGroup(entry) ? (
                      <SidebarGroup
                        key={entry.id}
                        group={entry}
                        open={isGroupOpen(entry)}
                        mini={mini}
                        onToggle={() => setGroupToggles((t) => ({ ...t, [entry.id]: !isGroupOpen(entry) }))}
                        onNavigate={closeMobile}
                      />
                    ) : (
                      <li key={entry.menu}>
                        <SidebarLink
                          menu={entry.menu}
                          label={MENU_LABEL[entry.menu]}
                          mini={mini}
                          onNavigate={closeMobile}
                        />
                      </li>
                    ),
                  )}
                </Fragment>
              ))}
            </ul>
          </nav>
        </aside>
      </div>

      {/* Main Body (Header + Content) */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Top Navbar */}
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between gap-4 border-b border-slate-200/80 bg-white/90 px-4 backdrop-blur-md shadow-2xs md:px-6">
          {/* Toggle sidebar + Breadcrumb */}
          <div className="flex min-w-0 items-center gap-2">
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              aria-controls="cms-sidebar"
              aria-expanded={mobileOpen}
              aria-label="Buka menu"
              className="flex size-9 shrink-0 items-center justify-center rounded-xl text-slate-600 transition hover:bg-slate-100 md:hidden"
            >
              <svg
                className="size-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>
            <button
              type="button"
              onClick={toggleSidebar}
              aria-controls="cms-sidebar"
              aria-expanded={!collapsed}
              aria-label={collapsed ? 'Perluas sidebar' : 'Ciutkan sidebar'}
              title={collapsed ? 'Perluas sidebar' : 'Ciutkan sidebar'}
              className="mr-1 hidden size-9 items-center justify-center rounded-xl text-slate-500 transition hover:bg-slate-100 hover:text-slate-800 md:flex"
            >
              <svg
                className="size-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <rect x="3.5" y="4.5" width="17" height="15" rx="2.5" strokeWidth={1.8} />
                <path strokeLinecap="round" strokeWidth={1.8} d="M9.5 4.5v15" />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.8}
                  d={collapsed ? 'M13.5 10l2 2-2 2' : 'M15.5 10l-2 2 2 2'}
                />
              </svg>
            </button>
            <span className="text-xs font-bold text-slate-400 uppercase tracking-wider hidden sm:inline">
              FOLA+ CMS /
            </span>
            <span className="truncate text-sm font-black text-slate-800">{currentLabel}</span>
          </div>

          {/* Topbar Right Actions */}
          <div className="ml-auto flex items-center gap-3 text-xs font-semibold">
            {isViewMode ? (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1 text-[11px] font-bold text-amber-800 border border-amber-200/80">
                <span className="size-1.5 rounded-full bg-amber-500 animate-pulse" />
                View Mode
              </span>
            ) : null}

            {/* Admin identity pill */}
            <div className="flex items-center gap-2.5 rounded-full bg-slate-100/80 border border-slate-200/60 px-3 py-1.5">
              <span className="flex size-6 items-center justify-center rounded-full bg-[#3851F0] text-[10px] font-black text-white">
                {me.name.charAt(0).toUpperCase()}
              </span>
              <span className="text-slate-800 font-bold" data-testid="admin-name">
                {me.name} <span className="text-slate-400 font-normal">· {me.role.name}</span>
              </span>
            </div>

            {/* Logout button */}
            <button
              type="button"
              className="flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3.5 py-1.5 font-bold text-slate-600 hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition cursor-pointer shadow-2xs"
              onClick={() => void logout()}
            >
              <svg
                className="size-3.5 text-slate-400 group-hover:text-red-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                />
              </svg>
              Keluar
            </button>
          </div>
        </header>

        {/* Content Body (Full-Width) */}
        <main className="min-w-0 flex-1 space-y-6 p-6 md:p-8 w-full">
          <Suspense fallback={<Loading />}>
            <Outlet />
          </Suspense>
        </main>
      </div>
    </div>
  );
  return isViewMode ? <ViewModeShield adminName={me.name}>{shell}</ViewModeShield> : shell;
}
